const util = (function () {
  /**
   * This function is used to create a request object for a jQuery ajax request
   * @param  {string} table  [table name]
   * @param  {string} fields [fields]
   * @param  {string} sort   [field(s) to sort by i.e. location_ndx ASC]
   * @param  {string/number} drupalUserid   [drupal userid to parse table by]
   * @param  {boolean} drupalUserSwitch   [whether the table shoudl be filtered by userid]
   * @return {object}        [jQuery ajax request object]
   */
  function createRequest(table, fields, sort, drupalUserid, drupalUserSwitch) {
    return {
      url: "./sites/all/scripts/activeDataFilters.php",
      data: {
        table: table,
        fields: [fields],
        sort: sort || "",
        drupal_userid: drupalUserid || 1,
        drupal_user_switch:
          typeof drupalUserSwitch === "undefined" ? false : drupalUserSwitch,
      },
    };
  }

  /**
   * This function converts a date to a year in YYYY format
   * @param  {date} date [date to parse]
   * @return {string}      [full year]
   */
  function returnYear(date) {
    return moment(date).format("YYYY");
  }

  /**
   * This function converts a date to a month name abbreviation
   * @param  {date} date [date to parse]
   * @return {string}      [month abbreviation]
   */
  function returnMonth(date) {
    return moment(date).format("MMM");
  }

  // returns a unique list month/year combinations
  // for use with a D3JS multi-series column chart
  // months represent the x-axis categories while
  // years represent the graph sereies
  // currently calculates a median value for each month/year
  function uniqueMonthsYears(dateField, resultField, unitfield, data) {
    var months = [], // x-axis categories
      years = [], // series
      uniqueSeries = []; // array to house final output

    // create an array of months present in the data
    months = [...new Set(data.map(d => returnMonth(d[dateField])))];

    // create array for years present in the data
    years = [...new Set(data.map(d => returnYear(d[dateField])))];

    // correctly order months
    months = sortMonth(months);

    let dataByYear = {};
    years.forEach(function (year) {
      dataByYear[year] = data.filter(d => returnYear(d[dateField]) === year);
    });

    // create an object for each month
    uniqueSeries = months.map(month => {
      let obj = {};
      obj.month = month;
      obj.unit = data[0][unitfield];
      years.forEach(year => {
        obj[year] = dataByYear[year]
          .filter(d => returnMonth(d[dateField]) === month)
          .map(d => +d[resultField]);
      });
      years.forEach(year => {
        obj[year] = d3.median(obj[year]);
      });
      return obj;
    });

    var keys = years.slice(0); // create keys, in this case the years present in the data
    uniqueSeries["columns"] = keys; // tack onto unique series object
    return uniqueSeries;
  } // uniqueMonthsYears

  // sort months by month abbreviations
  function sortMonth(months) {
    const monthOrder = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const newMonths = months.slice(0);
    newMonths.sort(function (a, b) {
      return monthOrder.indexOf(a) - monthOrder.indexOf(b);
    });
    return newMonths;
  } // sortMonth

  function addZeroToDate(val) {
    let integerDate = parseInt(val);
    let stringDate = val.toString();
    if (integerDate < 10) {
      stringDate = "0" + stringDate;
      return stringDate;
    } else {
      return val;
    }
  }

  function formatDate(date, format) {
    var newDate = new Date(`${date} 00:00:00`);
    if (format.toLowerCase() === "mm/dd/yyyy") {
      return (
        addZeroToDate(newDate.getMonth() + 1) +
        "/" +
        addZeroToDate(newDate.getDate()) +
        "/" +
        newDate.getFullYear()
      );
    } else if (format.toLowerCase() === "yyyy-mm-dd") {
      return (
        newDate.getFullYear() +
        "-" +
        addZeroToDate(newDate.getMonth() + 1) +
        "-" +
        addZeroToDate(newDate.getDate())
      );
    }
  } // formatDate

  /**
   * This function is used to return the current CCBWQA Water Year
   */
  function getWaterYear() {
    const today = moment();
    const month = today.month();
    let year = today.year();
    if ([9, 10, 11].includes(month)) {
      year = year + 1;
    }
    return year;
  }

  /**
   * This function is used to return the proper start/end dates
   * for a date filter based on the current water year
   */
  function setWaterYearDates() {
    const waterYear = getWaterYear();
    return [`${waterYear - 2}-10-01`, `${waterYear}-09-30`];
  }

  /**
   * This function returns an array of unique values from an array of objects for a specified field
   * @param  {string} field [object property to evaluate]
   * @param  {array} data  [data to evaluate]
   * @return {array}       [array containing unique values for specifice property]
   */
  function unique(field, data) {
    return [...new Set(data.map(d => d[field]))];
  }

  /**
   * This function returns an array of unique years from an array of objects for a specified date field
   * @param  {string} dateField [date field to evaluate]
   * @param  {array} data  [data to evaluate]
   * @return {array}       [array containing unique values for specificed date field]
   */
  function returnYearKeys(dateField, data) {
    return [...new Set(data.map(d => returnYear(d[dateField])))];
  }

  function calcUDStats(data) {
    const upstreamData = data.filter(d => d.endpoint_desc === "Upstream");
    const downstreamData = data.filter(d => d.endpoint_desc === "Downstream");
    let upstreamStats = {};
    let downstreamStats = {};
    let netStats = {};
    let pctStats = {};

    if (upstreamData.length > 0) {
      upstreamStats = {
        cat: `${upstreamData[0].location_name} (Upstream)`,
        avg: d3.mean(upstreamData, d => d.rvalue_converted),
        max: d3.max(upstreamData, d => d.rvalue_converted),
        min: d3.min(upstreamData, d => d.rvalue_converted),
      };
    }

    if (downstreamData.length > 0) {
      downstreamStats = {
        cat: `${downstreamData[0].location_name} (Downstream)`,
        avg: d3.mean(downstreamData, d => d.rvalue_converted),
        max: d3.max(downstreamData, d => d.rvalue_converted),
        min: d3.min(downstreamData, d => d.rvalue_converted),
      };
    }

    if (upstreamData.length > 0 && downstreamData.length > 0) {
      netStats = {
        cat: "Net Difference",
        avg: downstreamStats.avg - upstreamStats.avg,
        max: downstreamStats.max - upstreamStats.max,
        min: downstreamStats.min - upstreamStats.min,
      };
      pctStats = {
        cat: "% Difference",
        avg:
          ((downstreamStats.avg - upstreamStats.avg) / upstreamStats.avg) * 100,
        max:
          ((downstreamStats.max - upstreamStats.max) / upstreamStats.max) * 100,
        min:
          ((downstreamStats.min - upstreamStats.min) / upstreamStats.min) * 100,
      };
    }
    return [upstreamStats, downstreamStats, netStats, pctStats];
  }

  /**
   * Utility function used to resize the sidebars after each chart render
   */
  const resizeSidebar = () => {
    const $sidebar = document.querySelector("#annual-report-nav-wrapper");
    const $leftSection = document.querySelector(
      ".annual-report-monitoring-left-section"
    );
    const body = document.body;
    const html = document.documentElement;

    const height = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.clientHeight,
      html.scrollHeight,
      html.offsetHeight
    );

    $sidebar.style.height = `${height + 76}px`;
    $leftSection.style.height = `${height}px`;
  };

  return {
    createRequest,
    uniqueMonthsYears,
    returnMonth,
    returnYear,
    sortMonth,
    getWaterYear,
    setWaterYearDates,
    unique,
    returnYearKeys,
    formatDate,
    addZeroToDate,
    calcUDStats,
    resizeSidebar,
  };
})();
